import { LinkError } from '@apollo/client/errors'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatTabsModule } from '@angular/material/tabs'
import { ActivatedRoute, Router } from '@angular/router'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import { KpiImportKind } from '../../../../_generated/graphql'
import { MessageDialogService } from '../../../components/message-dialog/message-dialog.service'
import {
  KpiDashboardDefinition,
  KpiDashboardService,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'
import { captureException } from '../../../util/captureException'
import { handleDashboardNotFound } from '../handle-dashboard-not-found'
import { KpiDashboardImportTabComponent } from './kpi-dashboard-import-tab.component'
import { KpiEmployeeNumberSettingsComponent } from './kpi-employee-number-settings.component'
import { KpiKeyActionSettingsComponent } from './kpi-key-action-settings.component'
import { KpiOrgTargetSettingsComponent } from './kpi-org-target-settings.component'
import { KpiKnowledgeSettingsComponent } from './kpi-knowledge-settings.component'

@UntilDestroy()
@Component({
  selector: 'app-kpi-dashboard-settings',
  standalone: true,
  imports: [
    KpiDashboardImportTabComponent,
    KpiEmployeeNumberSettingsComponent,
    KpiKeyActionSettingsComponent,
    KpiKnowledgeSettingsComponent,
    KpiOrgTargetSettingsComponent,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTabsModule,
  ],
  templateUrl: './kpi-dashboard-settings.component.html',
  styleUrl: './kpi-dashboard-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiDashboardSettingsComponent implements OnInit {
  readonly importKind = KpiImportKind

  dashboard: KpiDashboardDefinition | null = null
  isLoading = true

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private kpiDashboardService: KpiDashboardService,
    private messageDialogService: MessageDialogService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const dashboardId = this.route.snapshot.paramMap.get('dashboardId')
    if (!dashboardId) {
      this.isLoading = false
      this.showLoadError(new Error('dashboardId is missing'))
      return
    }

    this.kpiDashboardService
      .getDashboard(dashboardId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (dashboard) => {
          this.dashboard = {
            ...dashboard,
            items: [...dashboard.items].sort(
              (left, right) => left.sortOrder - right.sortOrder,
            ),
          }
          this.isLoading = false
          this.changeDetectorRef.markForCheck()
        },
        error: (error: unknown) => {
          this.isLoading = false
          this.showLoadError(error)
          this.changeDetectorRef.markForCheck()
        },
      })
  }

  private showLoadError(error: unknown): void {
    if (handleDashboardNotFound(error, this.router, this.snackBar)) return
    captureException(error)
    // リンク由来（通信断・非 2xx 応答）のエラーは英語の技術的な文言なので定型文にする
    this.messageDialogService.showError(
      error instanceof Error && !LinkError.is(error) && error.message
        ? error.message
        : '個人支援ボード設定の読み込みに失敗しました。',
      {
        title: '読み込みエラー',
      },
    )
  }
}
